import {
  GoogleSignin,
  isErrorWithCode,
  isSuccessResponse,
  statusCodes,
} from '@react-native-google-signin/google-signin';
import { Platform } from 'react-native';

const GOOGLE_WEB_CLIENT_ID = process.env['EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID'] ?? '';
const GOOGLE_IOS_CLIENT_ID = process.env['EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID'] ?? '';

let isConfigured = false;

type NativeGoogleProfile = {
  email: string;
  fullName: string | null;
  givenName: string | null;
  familyName: string | null;
  photo: string | null;
};

type NativeGoogleSignInResult =
  | { cancelled: true }
  | {
      cancelled: false;
      accessToken: string;
      idToken: string;
      profile: NativeGoogleProfile;
    };

export function canUseNativeGoogleSignIn() {
  if (Platform.OS === 'android') {
    return GOOGLE_WEB_CLIENT_ID.length > 0;
  }

  if (Platform.OS === 'ios') {
    return GOOGLE_WEB_CLIENT_ID.length > 0 && GOOGLE_IOS_CLIENT_ID.length > 0;
  }

  return false;
}

function configureNativeGoogleSignIn() {
  if (Platform.OS === 'web' || isConfigured) {
    return;
  }

  GoogleSignin.configure({
    webClientId: GOOGLE_WEB_CLIENT_ID,
    ...(Platform.OS === 'ios' && GOOGLE_IOS_CLIENT_ID
      ? { iosClientId: GOOGLE_IOS_CLIENT_ID }
      : {}),
    profileImageSize: 120,
  });

  isConfigured = true;
}

export async function signInWithNativeGoogle(): Promise<NativeGoogleSignInResult> {
  if (!canUseNativeGoogleSignIn()) {
    if (Platform.OS === 'ios') {
      throw new Error(
        'Native Google Sign-In is not configured for iOS. Set EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID and EXPO_PUBLIC_GOOGLE_IOS_URL_SCHEME.',
      );
    }

    throw new Error('Native Google Sign-In is not configured.');
  }

  configureNativeGoogleSignIn();

  if (Platform.OS === 'android') {
    await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
  }

  const response = await GoogleSignin.signIn();

  if (!isSuccessResponse(response)) {
    return { cancelled: true };
  }

  const tokens = await GoogleSignin.getTokens();
  const idToken = response.data.idToken ?? tokens.idToken;

  if (!idToken) {
    throw new Error('Google did not return an ID token.');
  }

  return {
    cancelled: false,
    accessToken: tokens.accessToken,
    idToken,
    profile: {
      email: response.data.user.email,
      fullName: response.data.user.name,
      givenName: response.data.user.givenName,
      familyName: response.data.user.familyName,
      photo: response.data.user.photo,
    },
  };
}

export function getNativeGoogleSignInErrorMessage(error: unknown) {
  if (isErrorWithCode(error)) {
    switch (error.code) {
      case statusCodes.SIGN_IN_CANCELLED:
        return 'Sign-in was canceled.';
      case statusCodes.IN_PROGRESS:
        return 'Google sign-in is already in progress.';
      case statusCodes.PLAY_SERVICES_NOT_AVAILABLE:
        return 'Google Play Services are not available on this device.';
      default:
        return error.message;
    }
  }

  return error instanceof Error ? error.message : 'Failed to complete sign-in.';
}
